import * as client from "../../../client";
import {
  originalBuildingDescriptionFromRawAddress,
  originalConstructionDescriptionFromRawAddress,
} from "./EditSovModal";

type FlagIds = client.Address["private_flags"][string][number];

export const severityForFlag = (flagId: FlagIds): number => {
  if (typeof flagId === "string") {
    switch (flagId) {
      case "date_parsing_error":
        return 65;
      case "missing_coverage_headers":
        return 90;
      case "number_parsing_error":
        return 70;
      case "suspicious_year_warning":
        return 65;
      case "general_mismatch":
        return 70;
      case "large_number_warning":
        return 90;
      case "low_confidence":
        return 81;
      case "medium_confidence":
        return 65;
      case "year_out_of_range_warning":
        return 64;
      case "cooking_class_guess":
        return 65;
      case "non_property_insurance_value":
        return 65;
      case "unrecognized_central_alarm_value":
        return 65;
      case "unrecognized_yes_no_value":
        return 65;
      case "unrecognized_sprinkler_value":
        return 65;
      case "low_sprinkler_percentage":
        return 60;
      case "zip_parsing_error":
        return 85;
      case "blank_tiv_value":
        return 90;
      case "address_corrected_by_geocoding":
        return 0;
      case "non_ascii_removed":
        return 60;
      case "geocoding_failed":
        return 60;
      case "location_number_mismatch":
        return 60;
      case "low_aop_deductible":
        return 85;
      case "autofilled_aop_deductible":
        return 60;
      case "year_defaulted_to_year_built":
        return 60;
      case "approximate_geocoding":
        return 70;
      case "high_protection_class":
        return 90;
      case "tier_1_wind_zone":
        return 90;
    }
  }

  switch (flagId.flag_id) {
    case "unkonwn_coverage_column":
      return 90;
    case "non_property_insurance_value_v2":
      return 65;
    case "address_corrected_by_llm_parse":
      return 75;
    case "address_flagged_by_geocoding":
      return 75;
    case "coverage_computed_from_column_sum":
      return 90;
  }
};

export const maxSeverityForAddressFlags = (
  address: client.Address,
  addressField: keyof client.RawAddress | undefined
): number => {
  if (addressField === undefined) {
    return 0;
  }
  let maxSeverity = undefined;
  for (const flagId of address.private_flags[addressField] ?? []) {
    const newSeverity = severityForFlag(flagId);

    if (newSeverity > (maxSeverity ?? 0)) {
      maxSeverity = newSeverity;
    }
  }
  return maxSeverity ?? 0;
};

const flagText = (
  flag: FlagIds,
  originalValue: string | null,
  computedValue: string | null | number | undefined
): string => {
  const safeOriginalValue = originalValue ?? "NONE GIVEN";
  const confidenceString =
    (originalValue ?? "") === "" || originalValue === "NONE GIVEN"
      ? computedValue === null ||
        computedValue === undefined ||
        computedValue === ""
        ? "No value was found so we left this blank."
        : "No value was found so we inferred a value from company context."
      : `The original value was "${safeOriginalValue}"`;

  if (typeof flag === "string") {
    switch (flag) {
      case "date_parsing_error":
        return `Failed to parse date: "${safeOriginalValue}"`;
      case "missing_coverage_headers":
        return "No coverage headers found that sum to the TIV. Defaulting all TIV to Building Value.";
      case "number_parsing_error":
        return `Failed to parse number value: "${safeOriginalValue}"`;
      case "suspicious_year_warning":
        return `Warning when extracting a 4 digit year from: "${safeOriginalValue}"`;
      case "general_mismatch":
        return `The extracted value looks different than the original: "${safeOriginalValue}"`;
      case "large_number_warning":
        return "This number looks too large to be correct.";
      case "low_confidence":
        return `We have low confidence in this value. ${confidenceString}`;
      case "medium_confidence":
        return `We have medium confidence in this value. ${confidenceString}`;
      case "year_out_of_range_warning":
        return `This year was adjusted up to 1905 to avoid issues in excel (originally, this value was "${safeOriginalValue}")`;
      case "cooking_class_guess":
        return "The Business Information on this location does not have a perfect Cooking Class match. We inferred Cooking Class from the Business Information.";
      case "non_property_insurance_value":
        return "We found a non-property insurance subject for this location in the Acord app.";
      case "unrecognized_central_alarm_value":
        return `Unrecognized alarm type. The original value was "${safeOriginalValue}"`;
      case "unrecognized_sprinkler_value":
        return `Unrecognized sprinkler type. The original value was "${safeOriginalValue}"`;
      case "low_sprinkler_percentage":
        return `We found a low sprinkler percentage. The original value was "${safeOriginalValue}"`;
      case "unrecognized_yes_no_value":
        return `Unrecognized yes/no value. The original value was "${safeOriginalValue}"`;
      case "location_number_mismatch":
        return `We found a mismatch on this location value. The original was "${safeOriginalValue}"`;
      case "zip_parsing_error":
        if (originalValue) {
          return `Failed to parse zip code: "${safeOriginalValue}"`;
        } else {
          return `Zip code not found`;
        }
      case "blank_tiv_value":
        return "A blank tiv value was found so we defaulted to $0. This can throw off other coverage values as we try to find columns that sum to tiv.";
      case "address_corrected_by_geocoding":
        if (originalValue) {
          return `This field was corrected by geocoding. The original value was "${safeOriginalValue}"`;
        } else {
          return `This field was filled by geocoding (not found in input)`;
        }
      case "non_ascii_removed":
        return `Some special (non-ASCII) characters were removed from this field. The original value was "${safeOriginalValue}"`;
      case "geocoding_failed":
        return "We were unable to verify this address.";
      case "low_aop_deductible":
        return "The AOP Deductible is lower than the recommended minimum of $5,000";
      case "autofilled_aop_deductible":
        return "No AOP Deductible was found so we defaulted to the recommended minimum of $5,000";
      case "year_defaulted_to_year_built":
        return "No year was found so we defaulted to the year built";
      case "approximate_geocoding":
        return "We geocoded this address, but only found approximate coordinates. This may introduce error on the street or map views.";
      case "high_protection_class":
        return "This protection class is higher than the recommended maximum";
      case "tier_1_wind_zone":
        return "This county is a tier 1 windzone";
    }
  }

  switch (flag.flag_id) {
    case "unkonwn_coverage_column":
      return `These column headers don't appear to be related to property insurance, but they were included in the spreadsheet's TIV calculation: ${flag.columns
        .map((column) => `"${column}"`)
        .join(", ")}`;
    case "non_property_insurance_value_v2":
      return `These insurance subjects don't appear to be related to property insurance, so we did not include them in the TIV calculation: ${flag.subjects
        .map((subject) => `"${subject}"`)
        .join(", ")}`;
    case "address_corrected_by_llm_parse":
      return `We have medium confidence in this value, which was extracted from "${flag.original}"`;
    case "address_flagged_by_geocoding":
      return `Our geocoding model flagged this field as potentially incorrect: Consider "${flag.geocoded}" instead`;
    case "coverage_computed_from_column_sum":
      return `We were unable to find a TIV column that was the sum of other coverage columns. We calculated TIV by adding the following columns: ${flag.columns
        .map((column) => `"${column}"`)
        .join(", ")}`;
  }
};

export const flagTextFor = (
  address: client.Address,
  addressField: keyof client.RawAddress | undefined,
  showOriginalValueIfNoFlags?: boolean,
  isRequiredAndIsBlank?: boolean
) => {
  if (addressField === undefined) {
    return undefined;
  }

  if (address.private_overrides[addressField]) {
    return "A user edited this value";
  }

  let originalValue = address.private_original_values[addressField];

  switch (addressField) {
    case "construction_code":
      originalValue = originalConstructionDescriptionFromRawAddress(address);
      break;
    case "occupancy_code":
      originalValue = originalBuildingDescriptionFromRawAddress(address);
      break;
    case "building_description":
      originalValue = originalBuildingDescriptionFromRawAddress(address);
      break;
    default:
      break;
  }

  const flagOnlyText = (address.private_flags[addressField] ?? [])
    .map((flag) =>
      flagText(
        flag,
        originalValue,
        address.private_computed_values[addressField]
      )
    )
    .concat(isRequiredAndIsBlank ? ["This field is required for rating"] : [])
    .map((text) => `- ${text}`)
    .join("\n");

  if (flagOnlyText === "" && showOriginalValueIfNoFlags) {
    if ((address.private_original_values[addressField] ?? "") === "") {
      return "No original value was given";
    }
    return `The original value was: "${address.private_original_values[addressField]}"`;
  }

  return flagOnlyText;
};
